import { type KeyboardEvent, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useUnsavedDraft } from "@/hooks/use-unsaved-draft";
import { LimitNote, NotSavedNote, overLimit } from "@/components/ui/Note";

/** A line as typed: its words, and the words beside its key when the line has a second field. */
export interface AddedLine {
  text: string;
  words?: string;
}

/**
 * The line being typed and its sending. Cleared at once, so a second line can be typed while
 * the first is sent; a refusal brings the words back, unless the attorney has typed over them.
 */
function useAddedLine(
  onAdd: (line: AddedLine, putBack: () => void) => void,
  {
    withWords,
    maxLength,
    detailMaxLength,
  }: { withWords: boolean; maxLength?: number; detailMaxLength?: number },
) {
  const [text, setText] = useState("");
  const [words, setWords] = useState("");
  // A refused line goes back into the fields when they are empty. When the attorney has
  // typed the next line since, it waits here instead, whole, so Retry sends exactly what
  // was refused and never a mix of the two.
  const [waiting, setWaiting] = useState<readonly AddedLine[]>([]);
  const [refusedInField, setRefusedInField] = useState(false);
  const fieldsRef = useRef({ text, words });
  fieldsRef.current = { text, words };
  const ready =
    text.trim() !== "" &&
    (!withWords || words.trim() !== "") &&
    !overLimit(text.trim(), maxLength) &&
    !overLimit(words.trim(), detailMaxLength);
  const send = (line: AddedLine) => {
    onAdd(line, () => {
      if (fieldsRef.current.text === "" && fieldsRef.current.words === "") {
        setText(line.text);
        setWords(line.words ?? "");
        setRefusedInField(true);
      } else {
        setWaiting((held) => [...held, line]);
      }
    });
  };
  const add = () => {
    if (!ready) return;
    setRefusedInField(false);
    send(
      withWords
        ? { text: text.trim(), words: words.trim() }
        : { text: text.trim() },
    );
    setText("");
    setWords("");
  };
  const retry = () => {
    const held = waiting;
    setWaiting([]);
    held.forEach(send);
    if (refusedInField) add();
  };
  const refused = refusedInField || waiting.length > 0;
  useUnsavedDraft(refused);
  return { text, setText, words, setWords, ready, refused, add, retry };
}

/**
 * A line typed in and added, by Enter or by its button, which stays closed while the line is
 * empty. With `detail`, a second field takes the line's words beside its key, and the button
 * stays closed until both are typed: a definition's term and what it means. Words past the
 * server's limit keep the button closed, with a note of how much to remove.
 *
 * The row stops short of the delete column: each add puts a new row where the button was, its
 * delete under the pointer, so a quick second press would otherwise delete a row.
 */
export function AddField({
  placeholder,
  detail,
  action,
  maxLength,
  detailMaxLength,
  onAdd,
}: {
  placeholder: string;
  /** The second field's placeholder and name, when the line has words beside its key. */
  detail?: string;
  action: string;
  /** The longest line the server takes. */
  maxLength?: number;
  /** The longest words beside the key the server takes. */
  detailMaxLength?: number;
  /** `putBack` is called if the server refuses the line: its words return to the field. */
  onAdd: (line: AddedLine, putBack: () => void) => void;
}) {
  const { text, setText, words, setWords, ready, refused, add, retry } =
    useAddedLine(onAdd, {
      withWords: detail !== undefined,
      maxLength,
      detailMaxLength,
    });
  const onEnter = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      add();
    }
  };
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 pr-12">
        <Input
          size="dense"
          placeholder={placeholder}
          aria-label={placeholder}
          value={text}
          className={detail === undefined ? undefined : "w-48 flex-none"}
          onChange={(event) => {
            setText(event.target.value);
          }}
          onKeyDown={onEnter}
        />
        {detail !== undefined && (
          <Input
            size="dense"
            placeholder={detail}
            aria-label={detail}
            value={words}
            onChange={(event) => {
              setWords(event.target.value);
            }}
            onKeyDown={onEnter}
          />
        )}
        <Button
          variant="secondary"
          size="dense"
          disabled={!ready}
          onClick={add}
        >
          {action}
        </Button>
      </div>
      {maxLength !== undefined && (
        <LimitNote length={text.trim().length} max={maxLength} />
      )}
      {detailMaxLength !== undefined && (
        <LimitNote length={words.trim().length} max={detailMaxLength} />
      )}
      <NotSavedNote failed={refused} onRetry={retry} />
    </div>
  );
}
